import React, { useEffect, useState, Suspense } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Pagination from "react-bootstrap/Pagination";
import { profileTypeEndPoint } from "@containers/CompanyProfile/utils/enum.js";
import LoaderImg from "../../../src/assets/images/loader.gif";
import NoSearchDataComponent from "./no-search-data";
import { captureException } from "@sentry/react";

const LazyTreeDiagram = React.lazy(() => import("@core/charts/tree-diagram"));

const TradingPartnerGraph = ({ profileType, profileId, category, profileName, subCategory, sortOrderValue, isParentSubs, type }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState({ data: null });
  const [mustNot, setMustNot] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [originalData, setOriginalData] = useState({ childs: [] });
  const [lastFetchedPage, setLastFetchedPage] = useState(1);

  const query = new URLSearchParams(window.location.search);
  const merged_id = query.get("mergedID");
  const is_Merged = query.get("isMerged");

  const fetchGraphData = async (pageInfo = {}) => {
    try {
      setIsLoading(true);
      const requestPayload = {
        profile_type: profileType,
        _id: profileId,
        category,
        slug: profileName,
        sub_category: subCategory,
        map_type: "",
        order: sortOrderValue,
        must_not: mustNot,
        isSubsidiaryIncluded: isParentSubs,
        isNetworkGraph: true,
      };

      if (is_Merged) {
        requestPayload.isMerged = is_Merged === "true" ? true : false;
        requestPayload.mergedID = +merged_id;
      }
      const { data: responseData } = await axios.post(profileTypeEndPoint.ports.getShipperDetails, requestPayload);

      if (responseData.status?.toUpperCase() === "SUCCESS" && responseData.data) {
        const { data } = responseData;

        if (pageInfo.page) {
          setOriginalData((prevData) => ({
            ...prevData,
            childs: [...prevData.childs, ...data.childs],
          }));
          setLastFetchedPage(pageInfo.page);
        } else {
          setOriginalData(data);
          setLastFetchedPage(1);
        }

        setTotalCount(data.trading_partner_count);

        if (data.must_not?.length > 0) {
          setMustNot(data.must_not);
        }

        const startIndex = (pageInfo.page || 1 - 1) * resultPerPage;
        const endIndex = startIndex + resultPerPage;
        updateCurrentView(startIndex, endIndex, pageInfo.page ? data : data);
        return data;
      } else {
        throw new Error("Invalid response status");
      }
    } catch (error) {
      captureException(error);
      toast.error("Something went wrong. Please try again later!");
    } finally {
      setIsLoading(false);
    }
  };

  const updateCurrentView = (startIndex, endIndex, data = originalData) => {
    if (!data) return;

    setGraphData({
      data: {
        key: data.key,
        slug: data.slug,
        _id: data._id,
        entity_id: data.entity_id,
        country_list: data.country_list,
        childs: data.childs?.slice(startIndex, endIndex) || [],
      },
    });
  };

  const handlePageChange = async (direction) => {
    let newPage = currentPage;
    const currentTotalItems = originalData.childs.length;

    if (direction === "next") {
      const needsMoreData = currentPage * resultPerPage + 10 >= currentTotalItems && currentTotalItems < totalCount;

      if (needsMoreData) {
        const nextPage = lastFetchedPage + 1;
        await fetchGraphData({ page: nextPage }, "pafinnation");
      }

      if (currentPage * resultPerPage < totalCount) {
        newPage = currentPage + 1;
      }
    } else if (direction === "prev" && currentPage > 1) {
      newPage = currentPage - 1;
    }

    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      const startIndex = (newPage - 1) * resultPerPage;
      const endIndex = startIndex + resultPerPage;
      updateCurrentView(startIndex, endIndex);
    }
  };

  useEffect(() => {
    fetchGraphData();
  }, []);

  const currentStartItem = currentPage * resultPerPage - (resultPerPage - 1);
  const currentEndItem = Math.min(currentPage * resultPerPage, totalCount);
  const showPagination = !(currentPage * resultPerPage >= totalCount && currentPage === 1);

  const renderHeader = () => (
    <div className="tw-flex tw-flex-row tw-m-0 tw-pt-2 tw-pb-2 tw-mb-3">
      <div className="tw-w-1/6 tw-text-center tw-flex tw-justify-start tw-items-baseline tw-p-0">
        <div className="graph-x-axis-description">{category === "importers" ? "Importer" : category === "exporters" ? "Exporter" : null}</div>
      </div>

      <div className="tw-w-1/4 tw-text-center tw-flex tw-justify-start tw-items-center tw-p-0">
        <div>{category === "importers" ? <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1658494959/intel/Importer-Exporter_Tab/importer-exporter-arrow-left_htx1fc.svg" alt="Left Arrow" /> : <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1658494959/intel/Importer-Exporter_Tab/importer-exporter-arrow-right_zodal2.svg" alt="Right Arrow" />}</div>
      </div>

      <div className="tw-w-1/6 tw-text-center tw-flex tw-justify-start tw-p-0">
        <div className="graph-x-axis-description">{category === "importers" ? "Exporters" : category === "exporters" ? "Importers" : null}</div>
      </div>

      <div className="tw-w-1/4 tw-text-center tw-flex tw-justify-center tw-items-center tw-p-0">
        <div>{category === "importers" ? <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1658494959/intel/Importer-Exporter_Tab/importer-exporter-arrow-right_zodal2.svg" alt="Right Arrow" /> : <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1658494959/intel/Importer-Exporter_Tab/importer-exporter-arrow-left_htx1fc.svg" alt="Left Arrow" />}</div>
      </div>

      <div className="tw-w-1/6 tw-text-center tw-flex tw-justify-end tw-items-end tw-p-0">
        <div className="graph-x-axis-description">{category === "importers" ? "Other Top 3 Importers" : category === "exporters" ? "Other Top 3 Exporters" : null}</div>
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className="tw-flex tw-justify-center tw-items-center mt-2">
      <span>
        Showing {currentStartItem}-{currentEndItem} of
      </span>
      <span className="tw-ml-1">
        <Pagination>
          <Pagination.Item className="page-item pagination-item">{totalCount}</Pagination.Item>
          {showPagination && (
            <>
              <Pagination.Prev disabled={currentPage === 1} className="page-item pagination-prev" onClick={() => handlePageChange("prev")} />
              <Pagination.Next disabled={currentPage * resultPerPage >= totalCount} className="page-item pagination-next" onClick={() => handlePageChange("next")} />
            </>
          )}
        </Pagination>
      </span>
    </div>
  );

  return (
    <>
      <div>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center mt-5">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        ) : (
          <div className="hr mt-2 mb-3">
            <div className="importer-exporter-graph-container">
              {graphData.data ? (
                <>
                  {renderHeader()}
                  <div>
                    <Suspense fallback={""}>
                      <LazyTreeDiagram data={graphData.data} differ={type}></LazyTreeDiagram>
                    </Suspense>
                    {totalCount > 0 && renderPagination()}
                  </div>
                </>
              ) : (
                <NoSearchDataComponent content={`No Unique ${type === "EXPORTER" ? "Exporter" : "Importer"} Found`} />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TradingPartnerGraph;
